import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, Calendar, CheckCircle, XCircle, Clock as ClockIcon, UserList, 
  Plus, CaretRight, Eye, Warning, CalendarPlus, CalendarCheck,
  ClockClockwise, ArrowClockwise, CircleNotch, MagnifyingGlass,
  Pencil, Check, X, CalendarBlank, Hourglass
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { ClientContextBanner, ClientContextIndicator } from '../../components/accountant/ClientContextBanner';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  present: 'bg-green-500/10 text-green-600 border-green-500/20',
  absent: 'bg-red-500/10 text-red-600 border-red-500/20',
  late: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  half_day: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  on_leave: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  holiday: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  weekend: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',
};

const leaveStatusColors = {
  pending: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  approved: 'bg-green-500/10 text-green-600 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
  cancelled: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',
};

const leaveTypes = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'maternity', label: 'Maternity Leave' },
  { value: 'paternity', label: 'Paternity Leave' },
  { value: 'emergency', label: 'Emergency Leave' },
  { value: 'compensatory', label: 'Compensatory Off' },
];

export default function AttendancePage() {
  const { token, user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showClockInModal, setShowClockInModal] = useState(false);
  const [showBulkAttendanceModal, setShowBulkAttendanceModal] = useState(false);
  const [showLeaveRequestModal, setShowLeaveRequestModal] = useState(false);
  const [showLeaveActionModal, setShowLeaveActionModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveActionNote, setLeaveActionNote] = useState('');
  
  // Form state
  const [clockInForm, setClockInForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in_time: '',
    notes: ''
  });
  
  const [leaveForm, setLeaveForm] = useState({
    employee_id: '',
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: '',
    half_day: false,
    half_day_period: 'morning'
  });
  
  const [bulkAttendance, setBulkAttendance] = useState({});
  
  const canManage = hasPermission('hr', 'edit') || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [empRes, statsRes] = await Promise.all([
        fetch(`${API}/api/hr/employees`, { headers }),
        fetch(`${API}/api/attendance/stats?date=${selectedDate}`, { headers })
      ]);
      
      if (empRes.ok) {
        const empData = await empRes.json();
        setEmployees(empData.filter(e => e.status === 'active'));
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      // Fetch attendance records for the selected date
      const attRes = await fetch(`${API}/api/attendance/records?start_date=${selectedDate}&end_date=${selectedDate}`, { headers });
      if (attRes.ok) {
        setAttendanceRecords(await attRes.json());
      }
      
      // Fetch leave requests
      const leaveRes = await fetch(`${API}/api/attendance/leave/requests`, { headers });
      if (leaveRes.ok) {
        setLeaveRequests(await leaveRes.json());
      }
      
      // Fetch pending leaves (for managers)
      if (canManage) {
        const pendingRes = await fetch(`${API}/api/attendance/leave/requests/pending`, { headers });
        if (pendingRes.ok) {
          const data = await pendingRes.json();
          setPendingLeaves(data.pending_requests || []);
        }
      }
      
    } catch (err) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, canManage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get attendance record for an employee on selected date
  const getEmployeeAttendance = (employeeId) => {
    return attendanceRecords.find(r => r.employee_id === employeeId);
  };

  // Clock In handler
  const handleClockIn = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/attendance/clock-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(clockInForm)
      });
      
      if (res.ok) {
        toast.success('Clocked in successfully');
        setShowClockInModal(false);
        setClockInForm({ employee_id: '', date: selectedDate, clock_in_time: '', notes: '' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to clock in');
      }
    } catch (err) {
      toast.error('Error clocking in');
    }
  };

  // Clock Out handler
  const handleClockOut = async (attendanceId) => {
    try {
      const clockOutTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      const res = await fetch(`${API}/api/attendance/clock-out/${attendanceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clock_out_time: clockOutTime })
      });
      
      if (res.ok) {
        toast.success('Clocked out successfully');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to clock out');
      }
    } catch (err) {
      toast.error('Error clocking out');
    }
  };

  // Leave Request handler
  const handleLeaveRequest = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/attendance/leave/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(leaveForm)
      });
      
      if (res.ok) {
        toast.success('Leave request submitted');
        setShowLeaveRequestModal(false);
        setLeaveForm({ employee_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '', half_day: false, half_day_period: 'morning' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to submit leave request');
      }
    } catch (err) {
      toast.error('Error submitting leave request');
    }
  };

  // Leave Action handler (approve/reject)
  const handleLeaveAction = async (action) => {
    if (!selectedLeaveRequest) return;
    
    if (action === 'reject' && !leaveActionNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/attendance/leave/requests/${selectedLeaveRequest.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ action, note: leaveActionNote })
      });
      
      if (res.ok) {
        toast.success(`Leave request ${action}d`);
        setShowLeaveActionModal(false);
        setSelectedLeaveRequest(null);
        setLeaveActionNote('');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || `Failed to ${action} leave request`);
      }
    } catch (err) {
      toast.error(`Error ${action}ing leave request`);
    }
  };

  // Bulk attendance handler
  const handleBulkAttendance = async () => {
    const records = Object.entries(bulkAttendance)
      .filter(([_, data]) => data.status)
      .map(([empId, data]) => ({
        employee_id: empId,
        date: selectedDate,
        status: data.status,
        clock_in: data.clock_in || null,
        clock_out: data.clock_out || null,
        notes: data.notes || null
      }));
    
    if (records.length === 0) {
      toast.error('No attendance to record');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/attendance/bulk-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(records)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowBulkAttendanceModal(false);
        setBulkAttendance({});
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to record attendance');
      }
    } catch (err) {
      toast.error('Error recording attendance');
    }
  };

  // Initialize bulk attendance for all employees
  const initBulkAttendance = () => {
    const initial = {};
    employees.forEach(emp => {
      const existing = getEmployeeAttendance(emp.id);
      if (!existing) {
        initial[emp.id] = {
          status: 'present',
          clock_in: '09:00',
          clock_out: '18:00',
          notes: ''
        };
      }
    });
    setBulkAttendance(initial);
    setShowBulkAttendanceModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <ClientContextBanner />
      
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Attendance & Leave
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Track daily attendance and manage leave requests
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setShowClockInModal(true)} data-testid="clock-in-btn">
                <ClockClockwise size={18} className="mr-2" />
                Clock In
              </Button>
              <Button variant="outline" onClick={initBulkAttendance} data-testid="bulk-attendance-btn">
                <UserList size={18} className="mr-2" />
                Bulk Mark
              </Button>
            </>
          )}
          <Button onClick={() => setShowLeaveRequestModal(true)} data-testid="leave-request-btn">
            <CalendarPlus size={18} className="mr-2" />
            Leave Request
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
        >
          <div className="card-tactile rounded-lg p-4">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Total Staff</p>
            <p className="text-2xl font-heading font-semibold mt-1">{stats.total_employees}</p>
          </div>
          <div className="card-tactile rounded-lg p-4 border-l-4 border-green-500">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-green-600">Present</p>
            <p className="text-2xl font-heading font-semibold mt-1 text-green-600">{stats.present}</p>
          </div>
          <div className="card-tactile rounded-lg p-4 border-l-4 border-red-500">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-red-600">Absent</p>
            <p className="text-2xl font-heading font-semibold mt-1 text-red-600">{stats.absent}</p>
          </div>
          <div className="card-tactile rounded-lg p-4 border-l-4 border-amber-500">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-amber-600">Late</p>
            <p className="text-2xl font-heading font-semibold mt-1 text-amber-600">{stats.late}</p>
          </div>
          <div className="card-tactile rounded-lg p-4 border-l-4 border-blue-500">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-blue-600">On Leave</p>
            <p className="text-2xl font-heading font-semibold mt-1 text-blue-600">{stats.on_leave}</p>
          </div>
          <div className="card-tactile rounded-lg p-4 border-l-4 border-purple-500">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-purple-600">Pending Leaves</p>
            <p className="text-2xl font-heading font-semibold mt-1 text-purple-600">{stats.pending_leave_requests}</p>
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
          <TabsList>
            <TabsTrigger value="daily" data-testid="tab-daily">
              <Calendar size={16} className="mr-2" />
              Daily View
            </TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves">
              <CalendarBlank size={16} className="mr-2" />
              Leave Requests
            </TabsTrigger>
            {canManage && pendingLeaves.length > 0 && (
              <TabsTrigger value="pending" data-testid="tab-pending" className="relative">
                <Hourglass size={16} className="mr-2" />
                Pending
                <Badge className="ml-2 bg-amber-500 text-white">{pendingLeaves.length}</Badge>
              </TabsTrigger>
            )}
          </TabsList>
          
          {activeTab === 'daily' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
                data-testid="date-picker"
              />
              <Button variant="ghost" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
                Today
              </Button>
            </div>
          )}
        </div>

        {/* Daily Attendance View */}
        <TabsContent value="daily">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <div className="p-4 border-b flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-employees"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff Code</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead className="hidden sm:table-cell">Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Clock In</TableHead>
                  <TableHead className="hidden md:table-cell">Clock Out</TableHead>
                  <TableHead className="hidden lg:table-cell">Hours</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees
                  .filter(emp => {
                    if (searchTerm) {
                      const term = searchTerm.toLowerCase();
                      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                      return fullName.includes(term) || emp.staff_code?.toLowerCase().includes(term);
                    }
                    return true;
                  })
                  .filter(emp => {
                    if (statusFilter === 'all') return true;
                    const attendance = getEmployeeAttendance(emp.id);
                    if (statusFilter === 'absent') return !attendance;
                    return attendance?.status === statusFilter;
                  })
                  .map((emp) => {
                    const attendance = getEmployeeAttendance(emp.id);
                    const status = attendance?.status || 'absent';
                    
                    return (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                        <TableCell className="font-mono text-sm">{emp.staff_code || '-'}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                            <p className="text-xs text-muted-foreground">{emp.designation || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{emp.department || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusColors[status]}>
                            {status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell font-mono">
                          {attendance?.clock_in || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell font-mono">
                          {attendance?.clock_out || '-'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {attendance?.hours_worked ? `${attendance.hours_worked}h` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!attendance && canManage && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setClockInForm({ ...clockInForm, employee_id: emp.id, date: selectedDate });
                                  setShowClockInModal(true);
                                }}
                                title="Clock In"
                              >
                                <ClockClockwise size={14} />
                              </Button>
                            )}
                            {attendance && !attendance.clock_out && canManage && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleClockOut(attendance.id)}
                                title="Clock Out"
                              >
                                <ClockIcon size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            
            {employees.length === 0 && (
              <div className="text-center py-12">
                <UserList size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No employees found</p>
              </div>
            )}
          </motion.div>
        </TabsContent>

        {/* Leave Requests View */}
        <TabsContent value="leaves">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden sm:table-cell">From</TableHead>
                  <TableHead className="hidden sm:table-cell">To</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaveRequests.map((req) => (
                  <TableRow key={req.id} data-testid={`leave-row-${req.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{req.staff_code}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{req.leave_type.replace('_', ' ')}</TableCell>
                    <TableCell className="hidden sm:table-cell">{req.start_date}</TableCell>
                    <TableCell className="hidden sm:table-cell">{req.end_date}</TableCell>
                    <TableCell>{req.days}{req.half_day ? ' (Half)' : ''}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={leaveStatusColors[req.status]}>
                        {req.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-[200px] truncate">
                      {req.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {leaveRequests.length === 0 && (
              <div className="text-center py-12">
                <CalendarBlank size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No leave requests</p>
              </div>
            )}
          </motion.div>
        </TabsContent>

        {/* Pending Approvals View */}
        <TabsContent value="pending">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden sm:table-cell">Period</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingLeaves.map((req) => (
                  <TableRow key={req.id} data-testid={`pending-leave-${req.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{req.staff_code}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{req.leave_type.replace('_', ' ')}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {req.start_date} - {req.end_date}
                    </TableCell>
                    <TableCell>{req.days}{req.half_day ? ' (Half)' : ''}</TableCell>
                    <TableCell className="hidden md:table-cell max-w-[200px] truncate">
                      {req.reason}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600"
                          onClick={() => {
                            setSelectedLeaveRequest(req);
                            setShowLeaveActionModal(true);
                          }}
                          data-testid={`approve-leave-${req.id}`}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600"
                          onClick={() => {
                            setSelectedLeaveRequest(req);
                            setShowLeaveActionModal(true);
                          }}
                          data-testid={`reject-leave-${req.id}`}
                        >
                          <XCircle size={14} className="mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {pendingLeaves.length === 0 && (
              <div className="text-center py-12">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <p className="text-muted-foreground">No pending leave requests</p>
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Clock In Modal */}
      <Dialog open={showClockInModal} onOpenChange={setShowClockInModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockClockwise size={20} />
              Clock In Employee
            </DialogTitle>
            <DialogDescription>Record clock-in time for an employee</DialogDescription>
          </DialogHeader>
          <ClientContextIndicator label="Recording for" />
          <form onSubmit={handleClockIn} className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={clockInForm.employee_id} onValueChange={(v) => setClockInForm({...clockInForm, employee_id: v})}>
                <SelectTrigger data-testid="clock-in-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.staff_code} - {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={clockInForm.date}
                  onChange={(e) => setClockInForm({...clockInForm, date: e.target.value})}
                  data-testid="clock-in-date"
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={clockInForm.clock_in_time}
                  onChange={(e) => setClockInForm({...clockInForm, clock_in_time: e.target.value})}
                  placeholder="HH:MM"
                  data-testid="clock-in-time"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={clockInForm.notes}
                onChange={(e) => setClockInForm({...clockInForm, notes: e.target.value})}
                placeholder="Any notes..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowClockInModal(false)}>Cancel</Button>
              <Button type="submit" disabled={!clockInForm.employee_id} data-testid="confirm-clock-in">
                Clock In
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Request Modal */}
      <Dialog open={showLeaveRequestModal} onOpenChange={setShowLeaveRequestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus size={20} />
              Leave Request
            </DialogTitle>
            <DialogDescription>Submit a leave request for approval</DialogDescription>
          </DialogHeader>
          <ClientContextIndicator label="Requesting for" />
          <form onSubmit={handleLeaveRequest} className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={leaveForm.employee_id} onValueChange={(v) => setLeaveForm({...leaveForm, employee_id: v})}>
                <SelectTrigger data-testid="leave-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.staff_code} - {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                <SelectTrigger data-testid="leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From</Label>
                <Input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})}
                  data-testid="leave-start"
                />
              </div>
              <div>
                <Label>To</Label>
                <Input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                  data-testid="leave-end"
                />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                placeholder="Reason for leave..."
                rows={3}
                data-testid="leave-reason"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLeaveRequestModal(false)}>Cancel</Button>
              <Button type="submit" disabled={!leaveForm.employee_id || !leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason} data-testid="submit-leave">
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Action Modal */}
      <Dialog open={showLeaveActionModal} onOpenChange={(open) => {
        setShowLeaveActionModal(open);
        if (!open) {
          setSelectedLeaveRequest(null);
          setLeaveActionNote('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck size={20} />
              Process Leave Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedLeaveRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee:</span>
                  <span className="font-medium">{selectedLeaveRequest.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Leave Type:</span>
                  <span className="capitalize">{selectedLeaveRequest.leave_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Period:</span>
                  <span>{selectedLeaveRequest.start_date} - {selectedLeaveRequest.end_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Days:</span>
                  <span>{selectedLeaveRequest.days}</span>
                </div>
              </div>
              
              <div className="p-3 bg-muted/20 rounded-lg">
                <p className="text-sm"><strong>Reason:</strong> {selectedLeaveRequest.reason}</p>
              </div>
              
              <div>
                <Label>Note (required for rejection)</Label>
                <Textarea
                  value={leaveActionNote}
                  onChange={(e) => setLeaveActionNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={2}
                  data-testid="leave-action-note"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLeaveActionModal(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => handleLeaveAction('reject')}
              data-testid="confirm-reject-leave"
            >
              <XCircle size={16} className="mr-1" />
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleLeaveAction('approve')}
              data-testid="confirm-approve-leave"
            >
              <CheckCircle size={16} className="mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Attendance Modal */}
      <Dialog open={showBulkAttendanceModal} onOpenChange={setShowBulkAttendanceModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserList size={20} />
              Bulk Mark Attendance - {selectedDate}
            </DialogTitle>
            <DialogDescription>Mark attendance for all employees at once</DialogDescription>
          </DialogHeader>
          <ClientContextIndicator label="Recording for" />
          
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {employees
              .filter(emp => !getEmployeeAttendance(emp.id))
              .map((emp) => (
                <div key={emp.id} className="flex items-center gap-4 p-3 bg-muted/20 rounded-lg">
                  <div className="min-w-[150px]">
                    <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                    <p className="text-xs text-muted-foreground">{emp.staff_code}</p>
                  </div>
                  <Select 
                    value={bulkAttendance[emp.id]?.status || 'present'} 
                    onValueChange={(v) => setBulkAttendance({...bulkAttendance, [emp.id]: {...bulkAttendance[emp.id], status: v}})}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="half_day">Half Day</SelectItem>
                      <SelectItem value="on_leave">On Leave</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="time"
                    value={bulkAttendance[emp.id]?.clock_in || '09:00'}
                    onChange={(e) => setBulkAttendance({...bulkAttendance, [emp.id]: {...bulkAttendance[emp.id], clock_in: e.target.value}})}
                    className="w-28"
                    placeholder="In"
                  />
                  <Input
                    type="time"
                    value={bulkAttendance[emp.id]?.clock_out || '18:00'}
                    onChange={(e) => setBulkAttendance({...bulkAttendance, [emp.id]: {...bulkAttendance[emp.id], clock_out: e.target.value}})}
                    className="w-28"
                    placeholder="Out"
                  />
                </div>
              ))}
          </div>
          
          {employees.filter(emp => !getEmployeeAttendance(emp.id)).length === 0 && (
            <p className="text-center text-muted-foreground py-8">All employees already have attendance for this date</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAttendanceModal(false)}>Cancel</Button>
            <Button onClick={handleBulkAttendance} disabled={Object.keys(bulkAttendance).length === 0} data-testid="save-bulk-attendance">
              <Check size={16} className="mr-1" />
              Save All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
